/* eslint-disable @typescript-eslint/naming-convention */
import Big from "big.js";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";
import { SystemConfig } from "../../../hooks/useSystemConfig";
import { convertDbDateToCenterTZ } from "../../../common/dateHelpers";

type SupabaseCenterConfig = Pick<
  DatabaseRow<"center_configs">,
  | "discount_amount"
  | "default_currency_code"
  | "default_offering_duration_minutes"
  | "appointment_checkout_duration_seconds"
  | "appointment_checkout_max_repeat_count"
>;

type SupabaseCenterAddress = DatabaseRow<"center_addresses">;

type SupabaseCenter = Pick<
  DatabaseRow<"centers">,
  | "id"
  | "name"
  | "country"
  | "city"
  | "postal_code"
  | "region"
  | "phone_number"
  | "email"
  | "fax_number"
  | "timezone"
  | "state"
> & {
  center_configs: SupabaseCenterConfig[];
  center_addresses: SupabaseCenterAddress[];
};

type SupabaseCenterOffering = Pick<
  DatabaseRow<"center_offerings">,
  | "id"
  | "offering_id"
  | "name"
  | "description"
  | "deposit_amount"
  | "price"
  | "duration_minutes"
  | "radiology_specialty"
>;

type SupabaseAppointmentOffering = Pick<
  DatabaseRow<"appointment_offerings">,
  "id" | "side"
> & {
  center_offering: SupabaseCenterOffering;
  appointment_add_ons: SupabaseAppointmentAddOn[];
};

type SupabaseCenterPackage = Pick<
  DatabaseRow<"center_packages">,
  "id" | "description" | "name"
>;

type SupabaseAppointmentPackage = {
  center_package: SupabaseCenterPackage;
  appointment_offerings: SupabaseAppointmentOffering[];
  appointment_add_ons: SupabaseAppointmentAddOn[];
};

type SupabaseCenterAddOn = Pick<
  DatabaseRow<"center_add_ons">,
  "id" | "price" | "name"
>;

type SupabaseAppointmentAddOn = {
  center_add_on: SupabaseCenterAddOn;
};

type SupabaseTimeSlot = Pick<DatabaseRow<"center_time_slots">, "started_at">;

type SupabaseAppointmentCustomerDetails = Pick<
  DatabaseRow<"appointment_customer_details">,
  "first_name" | "last_name" | "email" | "type"
>;

type SupabaseAppointmentPatientDetails = Pick<
  DatabaseRow<"appointment_patient_details">,
  "first_name" | "last_name" | "date_birth" | "email" | "mobile_phone_number"
>;

export type SupabaseConfirmedAppointment = Pick<
  DatabaseRow<"appointments">,
  | "id"
  | "checkout_repeat_count"
  | "status"
  | "total_cost_amount"
  | "discount_amount"
  | "confirmed_at"
  | "upsell_confirmed_at"
> & {
  center: SupabaseCenter;
  appointment_offerings: SupabaseAppointmentOffering[];
  appointment_packages: SupabaseAppointmentPackage[];
  appointment_add_ons: SupabaseAppointmentAddOn[];
  time_slot: SupabaseTimeSlot;
  appointment_customer_details: SupabaseAppointmentCustomerDetails[];
  appointment_patient_details: SupabaseAppointmentPatientDetails[];
};

export const supabaseConfirmedAppointmentRequest = `
id,
checkout_repeat_count,
status,
total_cost_amount,
discount_amount,
confirmed_at,
upsell_confirmed_at,
center:center_id (
  id,
  name,
  country,
  city,
  postal_code,
  region,
  phone_number,
  email,
  state,
  fax_number,
  timezone,
  center_configs (
    discount_amount,
    default_currency_code,
    default_offering_duration_minutes,
    appointment_checkout_duration_seconds,
    appointment_checkout_max_repeat_count
  ),
  center_addresses (
    address,
    type
  )
),
appointment_offerings (
  id,
  side,
  appointment_add_ons (
    center_add_on:center_add_on_id (
      id,
      name,
      type,
      price
    )
  ),
  center_offering:center_offering_id (
    id,
    offering_id,
    name,
    description,
    deposit_amount,
    price,
    duration_minutes,
    radiology_specialty
  )
),
appointment_packages (
  center_package:center_package_id (
    id,
    name,
    description
  ),
  appointment_add_ons (
    center_add_on:center_add_on_id (
      id,
      name,
      type,
      price
    )
  ),
  appointment_offerings (
    id,
    side,
    center_offering:center_offering_id (
      id,
      offering_id,
      name,
      description,
      deposit_amount,
      price,
      duration_minutes,
      radiology_specialty
    )
  )
),
appointment_add_ons (
  center_add_on:center_add_on_id (
    id,
    name,
    price
  )
),
time_slot:time_slot_id (
  started_at
),
appointment_customer_details (
  first_name,
  last_name,
  email
),
appointment_patient_details (
  first_name,
  last_name,
  date_birth,
  email,
  mobile_phone_number
)
`;

export type AppointmentOffering = {
  id: string;
  name: string;
  description: string;
  depositAmount: number;
  price: number;
  discountPrice: number;
  durationMinutes: number;
  side?: string;
  centerOfferingId: string;
  globalOfferingId: string;
  addOnIds: string[];
  radiologySpecialty: string | null;
};

export type AppointmentPackage = {
  name: string;
  description: string;
  depositAmount: number;
  price: number;
  durationMinutes: number;
  discountPrice: number;
  offerings: AppointmentOffering[];
  centerPackageId: string;
  addOnIds: string[];
};

export type AppointmentAddOn = {
  centerAddOnId: string;
  name: string;
  price: number;
};

export type Center = {
  id: string;
  name: string;
  country: string;
  city: string;
  postalCode: string;
  region: string;
  state: string;
  phoneNumber: string;
  email: string;
  faxNumber: string | null;
  discountAmount: number;
  defaultCurrencyCode: string;
  appointmentCheckoutDurationSeconds: number;
  appointmentCheckoutMaxRepeatCount: number;
  defaultOfferingDurationMinutes: number;
  addresses: DatabaseRow<"center_addresses">[];
};

export type AppointmentTimeSlot = {
  startedAt: Date;
};

type AppointmentCustomerDetails = {
  firstName: string;
  lastName: string;
  email: string | null;
  type: SupabaseAppointmentCustomerDetails["type"];
};

type AppointmentPatientDetails = {
  firstName: string;
  lastName: string;
  dateBirth: string;
  email: string | null;
  mobilePhoneNumber: string | null;
};

const parseOffering = (params: {
  id: string;
  centerOffering: SupabaseCenterOffering;
  defaultOfferingDurationMinutes: number;
  discountAmount: number;
  appointmentAddOns?: SupabaseAppointmentAddOn[];
  side?: string;
}): AppointmentOffering => {
  const {
    centerOffering,
    defaultOfferingDurationMinutes,
    discountAmount,
    appointmentAddOns,
    side,
    id,
  } = params;

  const {
    id: centerOfferingId,
    offering_id: globalOfferingId,
    name,
    description,
    deposit_amount,
    price,
    duration_minutes,
    radiology_specialty,
  } = centerOffering;

  const discountPrice = discountAmount
    ? Big(price).minus(discountAmount).toNumber()
    : price;

  if (!globalOfferingId) {
    throw new Error("Offering not found");
  }

  const addOnIds =
    appointmentAddOns?.map((addOn) => {
      return addOn.center_add_on.id;
    }) || [];

  return {
    id,
    centerOfferingId,
    name,
    description,
    depositAmount: deposit_amount,
    price,
    discountPrice,
    durationMinutes: duration_minutes ?? defaultOfferingDurationMinutes,
    globalOfferingId,
    side,
    addOnIds,
    radiologySpecialty: radiology_specialty,
  };
};

const calcPackageSums = (offerings: AppointmentOffering[]) => {
  const initialValues = {
    depositAmount: Big(0),
    price: Big(0),
    durationMinutes: Big(0),
  };

  const bigResults = offerings.reduce((acc, offering) => {
    return {
      depositAmount: acc.depositAmount.plus(offering.depositAmount),
      price: acc.price.plus(offering.price),
      durationMinutes: acc.durationMinutes.plus(offering.durationMinutes),
    };
  }, initialValues);

  return {
    depositAmount: bigResults.depositAmount.round(2).toNumber(),
    price: bigResults.price.round(2).toNumber(),
    durationMinutes: bigResults.durationMinutes.round(2).toNumber(),
  };
};

export type ConfirmedAppointment = {
  id: string;
  checkoutRepeatCount: number;
  status: SupabaseConfirmedAppointment["status"];
  center: Center;
  offerings: AppointmentOffering[];
  packages: AppointmentPackage[];
  addOns: AppointmentAddOn[];
  timeSlot: AppointmentTimeSlot;
  customerDetails: AppointmentCustomerDetails;
  patientDetails: AppointmentPatientDetails;
  totalPrice: number;
  confirmedAt?: string | null;
  upsellConfirmedAt?: string | null;
};

const parseConfirmedAppointment = (
  supabaseAppointment: SupabaseConfirmedAppointment,
  systemConfig: SystemConfig,
): ConfirmedAppointment => {
  const {
    checkout_repeat_count,
    status,
    center,
    appointment_offerings,
    appointment_packages,
    appointment_add_ons,
    time_slot,
    appointment_customer_details,
    appointment_patient_details,
    total_cost_amount,
    discount_amount,
    confirmed_at,
    upsell_confirmed_at,
  } = supabaseAppointment;

  const {
    id: centerId,
    name,
    country,
    city,
    postal_code,
    region,
    state,
    phone_number,
    fax_number,
    email,
    center_configs,
    timezone,
    center_addresses,
  } = center;

  const totalPrice = Big(total_cost_amount)
    .minus(discount_amount || 0)
    .toNumber();

  const appointmentCenter: Center = {
    id: centerId,
    name,
    country,
    city,
    postalCode: postal_code,
    region,
    addresses: center_addresses,
    state,
    phoneNumber: phone_number,
    faxNumber: fax_number,
    email,
    discountAmount:
      center_configs[0]?.discount_amount ?? systemConfig.discountAmount,

    defaultCurrencyCode:
      center_configs[0]?.default_currency_code ??
      systemConfig.defaultCurrencyCode,

    appointmentCheckoutDurationSeconds:
      center_configs[0]?.appointment_checkout_duration_seconds ??
      systemConfig.appointmentCheckoutDurationSeconds,

    appointmentCheckoutMaxRepeatCount:
      center_configs[0]?.appointment_checkout_max_repeat_count ??
      systemConfig.appointmentCheckoutMaxRepeatCount,

    defaultOfferingDurationMinutes:
      center_configs[0]?.default_offering_duration_minutes ??
      systemConfig.defaultOfferingDurationMinutes,
  };

  const defaultOfferingDurationMinutes =
    center_configs[0]?.default_offering_duration_minutes ??
    systemConfig.defaultOfferingDurationMinutes;

  const appointmentOfferings = appointment_offerings.map((offering) => {
    return parseOffering({
      id: offering.id,
      centerOffering: offering.center_offering,
      defaultOfferingDurationMinutes,
      discountAmount: appointmentCenter.discountAmount,
      side: offering.side || undefined,
      appointmentAddOns: offering.appointment_add_ons,
    });
  });

  const appointmentPackages = appointment_packages.map<AppointmentPackage>(
    (packageItem) => {
      const {
        center_package,
        appointment_offerings: packageOfferings,
        appointment_add_ons: packageAddOns,
      } = packageItem;

      const { name: packageName, description } = center_package;

      const offerings = packageOfferings.map((offering) => {
        return parseOffering({
          id: offering.id,
          centerOffering: offering.center_offering,
          defaultOfferingDurationMinutes,
          discountAmount: appointmentCenter.discountAmount,
        });
      });

      const { depositAmount, price, durationMinutes } =
        calcPackageSums(offerings);

      const discountPrice = appointmentCenter.discountAmount
        ? Big(price).minus(appointmentCenter.discountAmount).toNumber()
        : price;

      const centerPackageId = packageItem.center_package.id;

      const addOnIds = packageAddOns.map((addOn) => {
        return addOn.center_add_on.id;
      });

      return {
        centerPackageId,
        name: packageName,
        description,
        offerings,
        depositAmount,
        price,
        discountPrice,
        durationMinutes,
        addOnIds,
      };
    },
  );

  const appointmentAddOns = appointment_add_ons.map((addOn) => {
    const { name: addOnName, price, id } = addOn.center_add_on;
    return { name: addOnName, price, centerAddOnId: id };
  });

  const appointmentTimeSlot: AppointmentTimeSlot = {
    startedAt: convertDbDateToCenterTZ({
      dateString: time_slot.started_at,
      timezone,
    }),
  };

  const supabaseCustomerDetails = appointment_customer_details[0];

  if (!supabaseCustomerDetails) {
    throw new Error("Customer details is not defined");
  }

  const customerDetails: AppointmentCustomerDetails = {
    firstName: supabaseCustomerDetails.first_name,
    lastName: supabaseCustomerDetails.last_name,
    email: supabaseCustomerDetails.email,
    type: supabaseCustomerDetails.type,
  };

  const supabasePatientDetails = appointment_patient_details[0];

  if (!supabasePatientDetails) {
    throw new Error("Patient details is not defined");
  }

  const patientDetails: AppointmentPatientDetails = {
    firstName: supabasePatientDetails.first_name,
    lastName: supabasePatientDetails.last_name,
    dateBirth: supabasePatientDetails.date_birth || "",
    email: supabasePatientDetails.email,
    mobilePhoneNumber: supabasePatientDetails.mobile_phone_number,
  };

  return {
    id: supabaseAppointment.id,
    checkoutRepeatCount: checkout_repeat_count || 0,
    status,
    center: appointmentCenter,
    offerings: appointmentOfferings,
    packages: appointmentPackages,
    addOns: appointmentAddOns,
    timeSlot: appointmentTimeSlot,
    customerDetails,
    totalPrice,
    patientDetails,
    confirmedAt: confirmed_at,
    upsellConfirmedAt: upsell_confirmed_at,
  };
};

export default parseConfirmedAppointment;
